import { Box, Stack, styled } from '@mui/material';
import { HTMLAttributes, ReactNode, Ref } from 'react';

/** Class of a reply row; its parent card can tell a hovered reply from a hovered card. */
export const REPLY_ROW_CLASS = 'reply-row';
/** Elements with this class inside a row are shown only while the row is hovered. */
export const REPLY_ROW_HOVER_TARGET_CLASS = 'reply-row-hover-target';

/** Horizontal padding of every row of a thread, in theme spacing units. */
export const REPLY_ROW_PADDING_X = 1.5;
const AVATAR_SIZE = 3;

const ReplyRowRoot = styled(Box, { name: 'ReplyRow' })(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `${theme.spacing(AVATAR_SIZE)} minmax(0, 1fr)`,
  columnGap: theme.spacing(1),
  padding: theme.spacing(1.25, REPLY_ROW_PADDING_X),
  borderTop: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  [`& .${REPLY_ROW_HOVER_TARGET_CLASS}`]: {
    opacity: 0,
  },
  [`&:hover .${REPLY_ROW_HOVER_TARGET_CLASS}, &:focus-within .${REPLY_ROW_HOVER_TARGET_CLASS}`]:
    {
      opacity: 1,
    },
  '&:hover, &:focus-within': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MetaLine = styled(Stack, { name: 'ReplyRowMeta' })(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  columnGap: theme.spacing(1),
  minHeight: theme.spacing(AVATAR_SIZE),
  lineHeight: theme.spacing(AVATAR_SIZE),
  fontSize: theme.typography.overline.fontSize,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));

const AuthorName = styled('strong', { name: 'ReplyRowAuthor' })(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.custom.OV.sys.onBackground,
}));

// The timestamp brings its own type; in the meta line it takes the line's.
const MetaTime = styled('span', { name: 'ReplyRowTime' })({
  '& span': {
    fontSize: 'inherit',
    fontWeight: 'inherit',
  },
});

const ResolutionLabel = styled('span', { name: 'ReplyResolutionLabel' })(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.custom.OV.sys.success,
}));

export interface ReplyRowProps {
  avatar: ReactNode;
  authorName: string;
  /** The reply's time, already formatted. */
  timestamp: ReactNode;
  isEdited: boolean;
  /** Marks the reply that resolved its thread. */
  isResolution?: boolean;
  /** Hover actions, placed at the row's top right. */
  actions?: ReactNode;
  /** Shown under the body, e.g. reactions. */
  footer?: ReactNode;
  /** Event handlers for the whole row, e.g. a long press. */
  rootProps?: HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLDivElement> };
  children: ReactNode;
}

/** One reply of a thread: avatar, then the author line with the body and footer lined up under the name. */
export const ReplyRow = ({
  avatar,
  authorName,
  timestamp,
  isEdited,
  isResolution = false,
  actions,
  footer,
  rootProps,
  children,
}: ReplyRowProps) => (
  <ReplyRowRoot className={REPLY_ROW_CLASS} data-testid="reply-row" {...rootProps}>
    {avatar}
    <Box minWidth={0}>
      <MetaLine>
        <AuthorName>{authorName}</AuthorName>
        <MetaTime>{timestamp}</MetaTime>
        {isEdited ? <span>edited</span> : null}
        {isResolution ? <ResolutionLabel>Resolution</ResolutionLabel> : null}
      </MetaLine>
      {children}
      {footer}
    </Box>
    {actions}
  </ReplyRowRoot>
);
